import type React from "react"
import { MessageCircle, User, Bot } from "lucide-react"
import styles from "./conversationView.module.css"
import ProgressBar from "../../../../common/UI/ProgressBar"
import useFormattedDate from "../../../../common/hooks/useFormattedDate"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"

interface Message {
  inquiryId: string
  inquiryDate: string
  citizenId: string | null
  procedureTypeId: string | null
  question: string
  userInquiry: string
  inquiryForever: string
  inquiryDateTime: string
  inquiryGetDay: string
  inquiryStart: string
  inquiryGetMinute: string
  responseGeneratedDate: string
  responseGenerated: string
  userFeedback: string | null
}

interface ConversationViewerProps {
  messages: Message[]
  loading?: boolean
}

export const ConversationViewer: React.FC<ConversationViewerProps> = ({ messages, loading = false }) => {
  const hFormatDate = useFormattedDate()

  const getFeedbackIcon = (feedback: string | null) => {
    if (feedback === "positive") return <span className={styles.feedbackPositive}>👍</span>
    if (feedback === "negative") return <span className={styles.feedbackNegative}>👎</span>
    return <span className={styles.feedbackPending}>⏳</span>
  }

  if (loading) {
    return (
      <div className={styles.conversationContainer}>
        <ProgressBar />
      </div>
    )
  }

  return (
    <div className={styles.conversationContainer}>
      <div className={styles.headerTitle}>
        <MessageCircle className={styles.headerIcon} />
        <h3 className={styles.colorOnSurfaceVariant}>Conversación ({messages?.length || 0} mensajes)</h3>
      </div>

      {!messages || messages.length === 0 ? (
        <div className={styles.noMessages}>
          <MessageCircle className={styles.noMessagesIcon} />
          <p>No hay mensajes en esta conversación</p>
        </div>
      ) : (
        <div className={styles.messageList}>
          {messages.map((message, index) => {
            const isFirst = index === 0
            const senderLabel = isFirst ? "Sistema" : "Usuario"
            const userText =
              message.userInquiry === "a1#!4lpasddq414ef4312a"
                ? "inicio de sesión"
                : (message.userInquiry || "Sin mensaje")

            return (
              <div key={message.inquiryId || index} className={styles.messageCard}>
                <div className={styles.messageHeader}>
                  <div className={styles.messageHeaderInfo}>
                    <span className={styles.messageTitle}>Mensaje #{index + 1}</span>
                    <span className={styles.messageMeta}>ID: {message.inquiryId || "N/A"}</span>
                    <span className={styles.messageMeta}>
                      {hFormatDate(message.inquiryDate, "dd/MM/yyyy hh:mm:ss a")}
                    </span>
                  </div>
                  <div className={styles.messageHeaderFeedback}>
                    {getFeedbackIcon(message.userFeedback)}
                    {message.procedureTypeId && (
                      <span className={styles.procedureTag}>
                        {message.procedureTypeId.replace("_", " ")}
                      </span>
                    )}
                  </div>
                </div>

                <div className={styles.messageBody}>
                  {/* Bloque Usuario/Sistema */}
                  <div className={styles.messageRow}>
                    <div className={styles.avatarUser}>
                      {/* Si quieres cambiar el ícono del primer mensaje, descomenta: */}
                      {/* {isFirst ? <Bot className={styles.avatarIcon} /> : <User className={styles.avatarIcon} />} */}
                      <User className={styles.avatarIcon} />
                    </div>
                    <div className={styles.userMessage}>
                      <div className={styles.messageMetaLine}>
                        <span className={styles.senderLabel}>{senderLabel}</span>
                        <span className={styles.timestamp}>
                          {hFormatDate(message.inquiryDate, "dd/MM/yyyy hh:mm:ss a")}
                        </span>
                      </div>
                      <p>{userText}</p>
                    </div>
                  </div>

                  {/* Bloque Asistente */}
                  <div className={styles.messageRow}>
                    <div className={styles.avatarBot}>
                      <Bot className={styles.avatarIcon} />
                    </div>
                    <div className={styles.botMessage}>
                      <div className={styles.messageMetaLine}>
                        <span className={styles.senderLabel}>Asistente IA</span>
                        <span className={styles.timestamp}>
                          {hFormatDate(message.responseGeneratedDate, "dd/MM/yyyy hh:mm:ss a")}
                        </span>
                      </div>
                      <div className={styles.botContent}>
                        {message.responseGenerated ? (
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {message.responseGenerated}
                          </ReactMarkdown>
                        ) : (
                          "Sin respuesta"
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
